import type { Appointment } from '~/models/appointments'
import type { Container } from '~/models/containers'
import type { Truck } from '~/models/trucks'

/**
 * A roster lookup, shaped exactly like `useTrucksStore().getTruck` so callers can pass
 * that in directly. Injected rather than read here so this stays pure logic — a Pinia
 * store is not available in every context that needs to resolve green status.
 */
export type TruckLookup = (
  truckId: number | null,
  licensePlate: string
) => Truck | null

/**
 * Whether an appointment is booked on a confirmed green/EV truck.
 *
 * Two sources, in order:
 *  1. The terminal's own report on the appointment (`extra_data.green_truck`). This is
 *     authoritative, but only APM's legacy portal sets it — roughly three quarters of
 *     APM appointments come through the API portal and carry nothing.
 *  2. The truck's roster-sourced status, which is portal-independent and so covers the
 *     appointments the first source can't speak for.
 *
 * Unknown resolves to `false`, meaning no badge and no warning. Green status is
 * three-state and unknown is the common case, so callers must never present the result
 * as "confirmed not green".
 *
 * NOTE: the backend's `is_green_truck` filter reads source 2 only — it does not consult
 * `appointment_is_green()`. As of DEV-7672 no APM appointment disagrees between the two,
 * so the server-filtered Imports list and this function agree; a divergence would show up
 * as a row carrying a leaf badge that the filter drops.
 */
export function appointmentIsOnGreenTruck(
  appointment: Appointment | null | undefined,
  lookupTruck: TruckLookup
): boolean {
  if (!appointment) return false
  const reportedByTerminal = appointment.is_green_truck
  if (reportedByTerminal !== undefined) return reportedByTerminal
  if (!appointment.truck_license_plate_number) return false
  return (
    lookupTruck(appointment.truck_id, appointment.truck_license_plate_number)
      ?.isGreen === true
  )
}

/**
 * Whether a container has an appointment a driver can still show up for that is booked
 * on a green/EV truck — the client-side equivalent of the backend's `is_green_truck`
 * container filter, used to decide whether a live-updated container belongs in an
 * already-filtered list.
 *
 * Restricted to `scheduled`/`in_progress` for the same reason the backend restricts its
 * EXISTS clause: a cancelled or completed green appointment must not keep a container in
 * the results. It's an approximation in one respect — the backend can see every
 * appointment on the container, while the frontend list payload carries only the latest
 * one.
 */
export function containerIsOnGreenTruck(
  container: Container,
  lookupTruck: TruckLookup
): boolean {
  const appointment = container.latest_appointment
  if (!appointment) return false
  if (!appointment.isScheduled && !appointment.isInProgress) return false
  return appointmentIsOnGreenTruck(appointment, lookupTruck)
}
